import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { meetingAPI } from '../services/api';
import SuperUserPanel from '../components/SuperUserPanel';
import MeetingHorizonBar from '../components/MeetingHorizonBar';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import FeedbackBanner from '../components/FeedbackBanner';
import './Dashboard.css';

function dtLocal(offsetMs = 0) {
  const d = new Date(Date.now() + offsetMs);
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function getUserTz() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const short = new Date().toLocaleTimeString('en', { timeZoneName: 'short' }).split(' ').pop();
    return { tz, short };
  } catch { return { tz: 'UTC', short: 'UTC' }; }
}

function getMeetingStatusBadge(meeting) {
  if (meeting.status === 'active') return { text: '🔴 Live', cls: 'badge-active' };
  if (meeting.status === 'ended')  return { text: 'Ended',  cls: 'badge-ended' };

  if (meeting.meeting_kind === 'planned' && meeting.scheduled_for) {
    if (meeting.scheduled_end && new Date(meeting.scheduled_end) < new Date()) {
      return { text: 'Expired', cls: 'badge-ended' };
    }
    const timeLeft = new Date(meeting.scheduled_for) - Date.now();
    if (timeLeft > 0) return { text: 'Upcoming', cls: 'badge-upcoming' };
    return { text: "It's Time!", cls: 'badge-join-now' };
  }

  return { text: 'Ready', cls: 'badge-ready' };
}

function formatScheduledFor(iso) {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatRoomId(code) {
  if (!code) return null;
  const u = code.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  return `VM-${u.slice(0, 4)}-${u.slice(4, 8)}`;
}

function formatDuration(startedAt, endedAt) {
  if (!startedAt || !endedAt) return null;
  const mins = Math.round((new Date(endedAt) - new Date(startedAt)) / 60000);
  if (mins < 1) return '< 1m';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const AVATAR_COLORS = ['#7c3aed','#0891b2','#d97706','#dc2626','#059669','#db2777'];

function AvatarStack({ count, hostName, max = 4 }) {
  const shown = Math.min(Math.max(count || 1, 1), max);
  const overflow = Math.max(0, (count || 1) - max);
  return (
    <div className="avatar-stack">
      {Array.from({ length: shown }).map((_, i) => (
        <div key={i} className="avatar-stack__dot"
          style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length], zIndex: shown - i }}>
          {i === 0 && hostName ? hostName[0].toUpperCase() : ''}
        </div>
      ))}
      {overflow > 0 && <div className="avatar-stack__overflow">+{overflow}</div>}
    </div>
  );
}

function LiveWaveform() {
  return (
    <div className="live-waveform">
      {[6, 12, 18, 10, 20, 8, 16, 12, 18, 6].map((h, i) => (
        <div key={i} className="live-waveform__bar"
          style={{ '--wh': `${h}px`, '--wd': `${i * 0.1}s` }} />
      ))}
    </div>
  );
}

function Dashboard() {
  const { user, logout, trialDaysRemaining, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [meetings, setMeetings]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [meetingKind, setMeetingKind]       = useState(null);
  const [newMeetingTitle, setNewMeetingTitle] = useState('');
  const [scheduledFor, setScheduledFor]     = useState('');
  const [scheduledEnd, setScheduledEnd]     = useState('');
  const [creating, setCreating]             = useState(false);
  const [launchingId, setLaunchingId]       = useState(null);
  const userTz = useMemo(() => getUserTz(), []);

  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const avatarMenuRef = useRef(null);
  const [searchQuery, setSearchQuery]       = useState('');
  const [activeFilter, setActiveFilter]     = useState('all');

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  useEffect(() => { loadMeetings(); }, []);

  // Refresh plan + meetings when user returns to tab (e.g. after paying via GPay or returning from a meeting)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') { refreshUser(); loadMeetings(); }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [refreshUser]);

  // Poll every 30s only while there are active meetings — keeps Live/Ended badges accurate
  useEffect(() => {
    const hasActiveMeetings = meetings.some(m => m.status === 'active');
    if (!hasActiveMeetings) return;
    const interval = setInterval(loadMeetings, 30000);
    return () => clearInterval(interval);
  }, [meetings]);

  // Show one-time upgrade toast after payment confirmed
  useEffect(() => {
    if (!user || user.plan === 'trial' || !user.plan_started_at) return;
    const minutesAgo = (Date.now() - new Date(user.plan_started_at).getTime()) / 60000;
    if (minutesAgo < 10) {
      const shownKey = `plan_toast_${user.plan_started_at}`;
      if (!localStorage.getItem(shownKey)) {
        localStorage.setItem(shownKey, '1');
        const planLabel = user.plan.charAt(0).toUpperCase() + user.plan.slice(1);
        toast.success(`Welcome to ${planLabel} plan! Payment confirmed.`);
      }
    }
  }, [user?.plan_started_at]);

  const loadMeetings = async () => {
    try {
      const res = await meetingAPI.list();
      setMeetings(res.data.meetings);
    } catch {
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  const resetCreateModal = () => {
    setMeetingKind(null);
    setNewMeetingTitle('');
    setScheduledFor('');
    setScheduledEnd('');
    setCreating(false);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    resetCreateModal();
  };

  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    const trimmed = newMeetingTitle.trim();
    if (!trimmed || trimmed.length < 3) { toast.error('Title must be at least 3 characters'); return; }
    if (trimmed.length > 100) { toast.error('Title too long (max 100 chars)'); return; }

    if (meetingKind === 'planned') {
      if (!scheduledFor) { toast.error('Pick a start time for the meeting'); return; }
      if (new Date(scheduledFor) <= new Date(Date.now() + 4 * 60 * 1000)) {
        toast.error('Schedule at least 5 minutes ahead'); return;
      }
      if (!scheduledEnd) { toast.error('Pick an end time for the meeting'); return; }
      if (new Date(scheduledEnd) <= new Date(scheduledFor)) {
        toast.error('End time must be after start time'); return;
      }
      if (new Date(scheduledEnd) - new Date(scheduledFor) < 15 * 60 * 1000) {
        toast.error('Meeting window must be at least 15 minutes'); return;
      }
    }

    setCreating(true);
    try {
      const payload = { title: trimmed };
      if (meetingKind === 'planned' && scheduledFor) {
        payload.scheduledFor = new Date(scheduledFor).toISOString();
        if (scheduledEnd) payload.scheduledEnd = new Date(scheduledEnd).toISOString();
      }

      const res = await meetingAPI.create(payload);
      closeCreateModal();
      await loadMeetings();

      if (meetingKind === 'planned') {
        toast.success('Meeting scheduled!');
      } else {
        toast.success('Meeting created!');
        if (res.data?.meeting) {
          setTimeout(() => { setSelectedMeeting(res.data.meeting); setShowInviteModal(true); }, 400);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create meeting');
      setCreating(false);
    }
  };

  const handleJoinMeeting = (meetingId) => navigate(`/meeting/${meetingId}`);

  const handleLaunch = async (meetingId) => {
    setLaunchingId(meetingId);
    try {
      await meetingAPI.launch(meetingId);
      await loadMeetings();
      toast.success('Meeting converted to Quick Meet — join when ready!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to launch meeting');
    } finally {
      setLaunchingId(null);
    }
  };

  const handleLaunchAndJoin = async (meetingId) => {
    setLaunchingId(meetingId);
    try {
      await meetingAPI.launch(meetingId);
      navigate(`/meeting/${meetingId}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to launch meeting');
      setLaunchingId(null);
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Remove this expired meeting from your dashboard? Your recordings will not be affected.')) return;
    try {
      await meetingAPI.deleteMeeting(meetingId);
      setMeetings(prev => prev.filter(m => m.id !== meetingId));
      toast.success('Meeting removed.');
    } catch (err) {
      toast.error('Failed to remove meeting.');
    }
  };

  const handleShowInvite = (meeting) => { setSelectedMeeting(meeting); setShowInviteModal(true); };

  const handleCopyInvite = () => {
    if (selectedMeeting) { navigator.clipboard.writeText(selectedMeeting.invite_url); toast.success('Link copied!'); }
  };

  const handleShareWhatsApp = async () => {
    if (!selectedMeeting) return;
    const msg = `Join my VoiceMeet Pro meeting!\n\nMeeting: ${selectedMeeting.title}\n\nJoin here: ${selectedMeeting.invite_url}\n\nNo account needed!`;
    // navigator.share auto-returns to app on Android after sharing
    if (navigator.share) {
      try {
        await navigator.share({ title: `VoiceMeet Pro: ${selectedMeeting.title}`, text: msg });
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
    // Desktop fallback — opens in new tab, current tab stays intact
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleShareFacebook = () => {
    if (!selectedMeeting) return;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(selectedMeeting.invite_url)}`, '_blank', 'width=600,height=400');
  };

  const handleShareEmail = () => {
    if (!selectedMeeting) return;
    const subject = `Join my VoiceMeet Pro meeting: ${selectedMeeting.title}`;
    const body = `Hi,\n\nJoin my meeting: ${selectedMeeting.title}\nLink: ${selectedMeeting.invite_url}\n\nNo account needed!`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleNativeShare = async () => {
    if (!selectedMeeting || !navigator.share) return;
    try {
      await navigator.share({ title: `VoiceMeet Pro: ${selectedMeeting.title}`, url: selectedMeeting.invite_url });
    } catch (err) {
      if (err.name !== 'AbortError') toast.error('Failed to share');
    }
  };

  const handleLogout = () => { logout(); toast.info('Logged out'); navigate('/'); };

  useEffect(() => {
    const handleOutside = (e) => {
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(e.target)) {
        setShowAvatarMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const filteredMeetings = useMemo(() => {
    let list = meetings;
    if (activeFilter === 'live')           list = list.filter(m => m.status === 'active');
    else if (activeFilter === 'scheduled') list = list.filter(m => m.meeting_kind === 'planned' && m.status !== 'ended');
    else if (activeFilter === 'ended')     list = list.filter(m => m.status === 'ended');
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(m => m.title.toLowerCase().includes(q));
    }
    return list;
  }, [meetings, activeFilter, searchQuery]);

  const getUserInitial = () => user?.name ? user.name.charAt(0).toUpperCase() : '?';

  const cardVariants = {
    hidden:  { opacity: 0, y: 20 },
    visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.38, ease: [0.4, 0, 0.2, 1] } }),
  };

  const tileVariants = {
    hidden:  { opacity: 0, y: 12 },
    visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.3 } }),
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-left">
          <div className="header-brand-icon">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/></svg>
          </div>
          <h1>VoiceMeet Pro</h1>
        </div>
        <div className="user-info" ref={avatarMenuRef}>
          <button
            className="avatar-btn"
            onClick={() => setShowAvatarMenu(v => !v)}
            aria-label="Account menu"
          >
            <div className="user-avatar">{getUserInitial()}</div>
            <span className={`plan-badge plan-badge--${user?.plan || 'trial'}`}>
              {user?.plan === 'pro' ? '⚡ Pro' : user?.plan === 'basic' ? 'Basic' : 'Trial'}
            </span>
          </button>
          <AnimatePresence>
            {showAvatarMenu && (
              <motion.div
                className="avatar-dropdown"
                initial={{ opacity: 0, y: -8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.96 }}
                transition={{ duration: 0.18 }}
              >
                <div className="avatar-dropdown__header">
                  <div className="avatar-dropdown__name">{user?.name}</div>
                  <div className="avatar-dropdown__email">{user?.email}</div>
                </div>
                <div className="avatar-dropdown__divider" />
                <button className="avatar-dropdown__item" onClick={() => { setShowAvatarMenu(false); navigate('/account'); }}>
                  <svg viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
                  Account
                </button>
                <button className="avatar-dropdown__item" onClick={() => { setShowAvatarMenu(false); navigate('/recordings'); }}>
                  <svg viewBox="0 0 24 24"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6zm-2 16c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/></svg>
                  Recordings
                </button>
                <button className="avatar-dropdown__item" onClick={() => { setShowAvatarMenu(false); navigate('/pricing'); }}>
                  <svg viewBox="0 0 24 24"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>
                  Plans
                </button>
                {user?.is_admin && (
                  <button className="avatar-dropdown__item" onClick={() => { setShowAvatarMenu(false); navigate('/admin'); }}>
                    <svg viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/></svg>
                    Admin Panel
                  </button>
                )}
                <div className="avatar-dropdown__divider" />
                <button className="avatar-dropdown__item avatar-dropdown__item--danger" onClick={() => { setShowAvatarMenu(false); handleLogout(); }}>
                  <svg viewBox="0 0 24 24"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/></svg>
                  Sign Out
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {user?.plan === 'trial' && trialDaysRemaining !== null && trialDaysRemaining > 0 && (
        <div className={`trial-banner ${trialDaysRemaining <= 3 ? 'trial-banner--urgent' : trialDaysRemaining <= 7 ? 'trial-banner--warning' : ''}`}>
          <span className="trial-banner__text">
            ⏳ {trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''} left in your free trial
          </span>
          <Link to="/pricing" className="trial-banner__cta">Upgrade now →</Link>
        </div>
      )}

      {/* Paywall — blocks dashboard when trial has expired */}
      {user?.plan === 'trial' && trialDaysRemaining === 0 && (
        <div className="trial-paywall">
          <div className="trial-paywall__card">
            <div className="trial-paywall__icon">⏰</div>
            <h2 className="trial-paywall__title">Your Free Trial Has Ended</h2>
            <p className="trial-paywall__desc">
              You've used your 14-day free trial. Upgrade to keep hosting meetings, recording calls, and using AI transcription.
            </p>
            <div className="trial-paywall__plans">
              <div className="paywall-plan">
                <span className="paywall-plan__name">Basic</span>
                <span className="paywall-plan__price">₹499<span>/mo</span></span>
                <span className="paywall-plan__detail">20 participants · 90 min · 25 recordings</span>
              </div>
              <div className="paywall-plan paywall-plan--pro">
                <span className="paywall-plan__badge">Popular</span>
                <span className="paywall-plan__name">Pro</span>
                <span className="paywall-plan__price">₹1299<span>/mo</span></span>
                <span className="paywall-plan__detail">100 participants · Unlimited · AI transcription</span>
              </div>
            </div>
            <Link to="/pricing" className="trial-paywall__btn">View Plans & Upgrade</Link>
            <p className="trial-paywall__note">No contracts · Cancel anytime · UPI & Cards accepted</p>
          </div>
        </div>
      )}

      <div className="dashboard-content">
        <FeedbackBanner />
        <div className="meetings-header">
          <div>
            <h2>Your Meetings</h2>
            <p className="meetings-count">
              <span className="meetings-count-pill">{meetings.length}</span> total meetings
            </p>
          </div>
          <motion.button
            onClick={() => setShowCreateModal(true)}
            className="btn-create-meeting"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
            ✨ New Meeting
          </motion.button>
        </div>

        {/* ── Search + Filter toolbar ── */}
        <div className="meetings-toolbar">
          <div className="search-wrap">
            <svg className="search-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
            <input
              className="search-input"
              type="text"
              placeholder="Search meetings…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>
          <div className="filter-chips">
            {[
              { key: 'all',       label: 'All' },
              { key: 'live',      label: '🔴 Live' },
              { key: 'scheduled', label: '🗓 Planned' },
              { key: 'ended',     label: 'Ended' },
            ].map(f => (
              <button
                key={f.key}
                className={`filter-chip ${activeFilter === f.key ? 'filter-chip--active' : ''}`}
                onClick={() => setActiveFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading meetings...</p>
          </div>
        ) : filteredMeetings.length === 0 ? (
          <motion.div className="empty-state" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="empty-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/></svg>
            </div>
            <h3>{meetings.length === 0 ? 'No meetings yet' : 'No matches'}</h3>
            <p>{meetings.length === 0 ? 'Create your first meeting to get started!' : 'Try a different search or filter.'}</p>
            {meetings.length === 0 && (
              <motion.button onClick={() => setShowCreateModal(true)} className="btn-primary" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                🎙️ Create Meeting
              </motion.button>
            )}
          </motion.div>
        ) : (
          <div className="meetings-grid">
            {filteredMeetings.map((meeting, index) => {
              const { text: badgeText, cls: badgeCls } = getMeetingStatusBadge(meeting);
              const isPlanned  = !!(meeting.meeting_kind === 'planned' && meeting.scheduled_for);
              const isEnded    = meeting.status === 'ended';
              const isLaunching = launchingId === meeting.id;

              // Window state for planned meetings — always boolean, never null
              const windowClosed = !!(isPlanned && meeting.scheduled_end &&
                new Date(meeting.scheduled_end) < new Date());
              const isItsTime = isPlanned && !windowClosed &&
                meeting.scheduled_for && new Date(meeting.scheduled_for) <= new Date();

              return (
                <motion.div
                  key={meeting.id}
                  className={`meeting-card ${isPlanned && !isEnded ? 'meeting-card--planned' : ''} ${windowClosed ? 'meeting-card--expired' : ''}`}
                  custom={index}
                  initial="hidden"
                  animate="visible"
                  variants={cardVariants}
                >
                  <div className="meeting-card-inner">
                    <div className="meeting-card-header">
                      <div className="meeting-card-title-row">
                        <h3>{meeting.title}</h3>
                        {!isEnded && (
                          <span className={`kind-chip kind-chip--${meeting.meeting_kind || 'instant'}`}>
                            {meeting.meeting_kind === 'planned' ? '🗓 Planned' : '⚡ Quick'}
                          </span>
                        )}
                      </div>
                      <span className={`status-badge ${badgeCls}`}>{badgeText}</span>
                    </div>

                    {/* Live waveform bar — active meetings only */}
                    {meeting.status === 'active' && (
                      <div className="meeting-live-bar">
                        <LiveWaveform />
                        <span className="meeting-rec-indicator">
                          <span className="meeting-rec-dot" /> REC
                        </span>
                      </div>
                    )}

                    <div className="meeting-card-body">
                      {isPlanned ? (
                        <div className="meeting-window-times">
                          <p className="meeting-scheduled-time">
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/></svg>
                            {formatScheduledFor(meeting.scheduled_for)}
                            {meeting.scheduled_end && (() => {
                              const startD = new Date(meeting.scheduled_for);
                              const endD   = new Date(meeting.scheduled_end);
                              const crossDay = startD.toDateString() !== endD.toDateString();
                              return (
                                <span className="meeting-window-end">
                                  → {crossDay
                                    ? endD.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                    : endD.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              );
                            })()}
                          </p>
                          <span className="meeting-tz-chip">{userTz.short}</span>
                        </div>
                      ) : (
                        <p className="meeting-date">
                          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"/></svg>
                          {new Date(meeting.created_at).toLocaleString()}
                        </p>
                      )}

                      {/* Avatar stack + participant line */}
                      <div className="meeting-participants-row">
                        <AvatarStack
                          count={isEnded ? (meeting.total_participants || 1) : (meeting.current_participants || 1)}
                          hostName={meeting.host_name}
                        />
                        <span className="meeting-participants-text">
                          {isEnded
                            ? `${meeting.total_participants || 0} attended · ${meeting.host_name} hosted`
                            : meeting.status === 'active'
                              ? `${meeting.current_participants || 0} in room · ${meeting.host_name} hosting`
                              : `Host: ${meeting.host_name}`}
                        </span>
                      </div>

                      {/* Duration — ended meetings */}
                      {isEnded && meeting.started_at && meeting.ended_at && (
                        <div className="meeting-duration-row">
                          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/></svg>
                          Duration: {formatDuration(meeting.started_at, meeting.ended_at)}
                        </div>
                      )}

                      {/* Room ID */}
                      {meeting.meeting_code && (
                        <div className="meeting-room-id-row">
                          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
                          {formatRoomId(meeting.meeting_code)}
                        </div>
                      )}
                    </div>

                    {/* Horizon Bar — planned meetings only */}
                    {isPlanned && !isEnded && (
                      <MeetingHorizonBar
                        scheduledFor={meeting.scheduled_for}
                        scheduledEnd={meeting.scheduled_end}
                        createdAt={meeting.created_at}
                      />
                    )}

                    {/* ── Actions ── */}
                    {isEnded ? (
                      <div className="meeting-card-actions">
                        <motion.button onClick={() => navigate(`/meeting-details/${meeting.id}`)}
                          className="btn-view" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
                          📋 View Details
                        </motion.button>
                      </div>
                    ) : isPlanned ? (
                      <div className="meeting-card-actions meeting-card-actions--planned">
                        {windowClosed ? (
                          /* Window expired — only show delete */
                          <>
                            <button className="btn-launch" disabled>
                              ⛔ Window Expired
                            </button>
                            <motion.button
                              onClick={() => handleDeleteMeeting(meeting.id)}
                              className="btn-delete-meeting"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.97 }}
                            >
                              🗑️ Remove
                            </motion.button>
                          </>
                        ) : (
                          /* Window still open */
                          <>
                            <motion.button
                              onClick={() => handleLaunch(meeting.id)}
                              className="btn-launch"
                              disabled={isLaunching}
                              whileHover={{ scale: isLaunching ? 1 : 1.02 }}
                              whileTap={{ scale: 0.97 }}
                            >
                              {isLaunching ? '⏳ Converting…' : '🚀 Launch Now'}
                            </motion.button>
                            <motion.button onClick={() => handleShowInvite(meeting)}
                              className="btn-invite" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
                              🔗 Invite
                            </motion.button>
                            {isItsTime ? (
                              <motion.button
                                onClick={() => handleLaunchAndJoin(meeting.id)}
                                className="btn-join"
                                disabled={isLaunching}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.97 }}
                              >
                                {isLaunching ? '⏳ Joining…' : '🎙️ Join Now'}
                              </motion.button>
                            ) : (
                              <button className="btn-join btn-join--locked" disabled title="Launch the meeting first to enable joining">
                                🔒 Join
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      /* Instant / active meeting: join directly */
                      <div className="meeting-card-actions">
                        <motion.button onClick={() => handleJoinMeeting(meeting.id)}
                          className="btn-join" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
                          🎙️ Join Meeting
                        </motion.button>
                        <motion.button onClick={() => handleShowInvite(meeting)}
                          className="btn-invite" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
                          🔗 Invite
                        </motion.button>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Create Meeting Modal ── */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div className="modal-overlay" onClick={closeCreateModal}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
          >
            <motion.div className="modal-content create-modal"
              onClick={e => e.stopPropagation()}
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.96 }}
              transition={{ duration: 0.28 }}
            >
              <h2 className="create-modal__title">New Meeting</h2>
              <p className="create-modal__sub">How would you like to meet?</p>

              {/* Type tiles */}
              <div className="meeting-type-tiles">
                {[
                  { kind: 'instant', emoji: '⚡', name: 'Quick Meet', desc: 'Start talking right now' },
                  { kind: 'planned', emoji: '🗓', name: 'Plan It',    desc: 'Schedule for a future time' },
                ].map((tile, i) => (
                  <motion.button
                    key={tile.kind}
                    className={`type-tile ${meetingKind === tile.kind ? 'type-tile--active' : ''}`}
                    onClick={() => setMeetingKind(tile.kind)}
                    custom={i}
                    variants={tileVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover={{ scale: 1.03, y: -2 }}
                    whileTap={{ scale: 0.97 }}
                    type="button"
                  >
                    <span className="type-tile__emoji">{tile.emoji}</span>
                    <span className="type-tile__name">{tile.name}</span>
                    <span className="type-tile__desc">{tile.desc}</span>
                    {meetingKind === tile.kind && (
                      <motion.div className="type-tile__check" initial={{ scale: 0 }} animate={{ scale: 1 }}>✓</motion.div>
                    )}
                  </motion.button>
                ))}
              </div>

              {/* Form — slides in when kind is chosen */}
              <AnimatePresence>
                {meetingKind && (
                  <motion.form
                    onSubmit={handleCreateMeeting}
                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                    animate={{ opacity: 1, height: 'auto', marginTop: '1.25rem' }}
                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div className="form-group">
                      <label>Meeting Title</label>
                      <input
                        type="text"
                        value={newMeetingTitle}
                        onChange={e => setNewMeetingTitle(e.target.value)}
                        placeholder={meetingKind === 'instant' ? 'e.g., Team Standup, Client Call' : 'e.g., Weekly Review, Project Kickoff'}
                        required
                        minLength={3}
                        maxLength={100}
                        autoFocus
                      />
                    </div>

                    {meetingKind === 'planned' && (
                      <>
                        <div className="form-group">
                          <label>Starts</label>
                          <input
                            type="datetime-local"
                            value={scheduledFor}
                            onChange={e => setScheduledFor(e.target.value)}
                            min={dtLocal(5 * 60 * 1000)}
                            required
                            className="input-datetime"
                          />
                          <span className="form-hint">Min 5 minutes from now</span>
                        </div>
                        <div className="form-group">
                          <label>Ends</label>
                          <input
                            type="datetime-local"
                            value={scheduledEnd}
                            onChange={e => setScheduledEnd(e.target.value)}
                            min={scheduledFor || dtLocal(20 * 60 * 1000)}
                            required
                            className="input-datetime"
                          />
                          <span className="form-hint">Min 15-minute window</span>
                        </div>
                        <div className="form-tz-row">
                          <span className="form-tz-icon">🌐</span>
                          <span className="form-tz-label">Times in your timezone:</span>
                          <span className="form-tz-chip">{userTz.short} · {userTz.tz}</span>
                        </div>
                      </>
                    )}

                    <div className="modal-buttons">
                      <motion.button
                        type="submit"
                        className={`btn-primary ${meetingKind === 'planned' ? 'btn-primary--planned' : ''}`}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        disabled={creating}
                      >
                        {creating
                          ? 'Creating…'
                          : meetingKind === 'instant'
                            ? '⚡ Start Now'
                            : '🗓 Schedule Meeting'}
                      </motion.button>
                      <button type="button" onClick={closeCreateModal} className="btn-secondary">Cancel</button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Invite Modal ── */}
      <AnimatePresence>
        {showInviteModal && selectedMeeting && (
          <motion.div className="modal-overlay" onClick={() => setShowInviteModal(false)}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
          >
            <motion.div className="modal-content invite-modal" onClick={e => e.stopPropagation()}
              initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }} transition={{ duration: 0.3 }}
            >
              <h2>📨 Share Meeting Invite</h2>
              <p className="modal-subtitle">{selectedMeeting.title}</p>
              <div className="invite-info">
                <p>✨ Share this link with anyone you want to invite.</p>
                <p>🌐 Guests can join without creating an account.</p>
              </div>
              <div className="invite-link-container">
                <input type="text" value={selectedMeeting.invite_url} readOnly className="invite-link-input" onClick={e => e.target.select()} />
                <motion.button onClick={handleCopyInvite} className="btn-copy" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
                  📋 Copy Link
                </motion.button>
              </div>
              <div className="share-buttons">
                <p className="share-label">Share via</p>
                <div className="share-btn-group">
                  <motion.button onClick={handleShareWhatsApp} className="share-btn whatsapp" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>💬 WhatsApp</motion.button>
                  <motion.button onClick={handleShareFacebook} className="share-btn facebook" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>📘 Facebook</motion.button>
                  <motion.button onClick={handleShareEmail}    className="share-btn email"    whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>✉️ Email</motion.button>
                  {navigator.share && (
                    <motion.button onClick={handleNativeShare} className="share-btn native" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>📤 Share…</motion.button>
                  )}
                </div>
              </div>
              <button onClick={() => setShowInviteModal(false)} className="btn-secondary" style={{ marginTop: '1rem', width: '100%' }}>Close</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <SuperUserPanel userEmail={user?.email} />
    </div>
  );
}

export default Dashboard;
